/**
 * Build a human-readable filename for a banker-recon uploaded document, e.g.
 * "LW26042311 - Darnov - Warehouse Recon.pdf".
 *
 * Used so warehouse-recon (and, later, purchase-advice) uploads are self-identifying in the doc list, on
 * reload, and in the storage object basename — instead of the raw uploaded filename.
 */

interface ReconNameLoan {
  trans_details_loan_number?: string | null;
  borrower_last_name?: string | null;
}

/**
 * Sanitize a single filename segment: drop reserved chars, collapse whitespace, trim, and strip trailing
 * dots/spaces (Windows-problem chars). Mirrors `resolveAttachmentFilename` in encompassDocumentService.
 * Returns '' when nothing usable survives.
 */
export function sanitizeNameSegment(raw: string | null | undefined): string {
  return (raw ?? '')
    .replace(/[/\\:*?"<>|]/g, '') // reserved characters
    .replace(/\s+/g, ' ')         // collapse internal whitespace
    .trim()
    .replace(/[. ]+$/, '');       // trailing dots/spaces
}

/** Make a fallback filename safe: strip any path, sanitize, then ensure a single `.pdf`. */
function normalizeFallbackName(name: string): string {
  const base = (name ?? '').replace(/^.*[/\\]/, ''); // basename only
  // Sanitize FIRST (strips trailing spaces/dots) so the `.pdf` strip can't be dodged by trailing whitespace —
  // otherwise "Final Recon.pdf " would round-trip to "Final Recon.pdf.pdf".
  const stem = sanitizeNameSegment(base).replace(/\.pdf$/i, '');
  return stem ? `${stem}.pdf` : 'document.pdf';
}

/**
 * `"{loanNumber} - {borrowerLastName} - {docLabel}.pdf"`. Each segment is sanitized; empty segments are
 * dropped. Falls back to `fallbackName` when neither the loan number nor the last name survives (so the name
 * can't collapse to just the doc label, which would be ambiguous across loans). Extension is fixed to `.pdf`.
 */
export function buildReconDocName(
  loan: ReconNameLoan | null | undefined,
  docLabel: string,
  fallbackName: string
): string {
  const parts = [
    sanitizeNameSegment(loan?.trans_details_loan_number),
    sanitizeNameSegment(loan?.borrower_last_name),
    sanitizeNameSegment(docLabel)
  ].filter(Boolean);

  // <= 1 means only the doc label survived (no loan number AND no last name) — too ambiguous to use.
  if (parts.length <= 1) {
    return normalizeFallbackName(fallbackName);
  }
  return `${parts.join(' - ')}.pdf`;
}
